/**
 * "Omat tilat" — landing for authenticated users.
 *
 * Three tabs:
 *   - Saadut jaot     → /smt/shares/incoming
 *   - Tekemäni jaot   → /smt/shares (outgoing)
 *   - Tilaukseni      → /smt/orders
 *
 * Replaces the M0 placeholder in views/placeholder-pages.ts. The placeholder
 * file is kept for the market page (M6).
 */
import { listIncoming, listOutgoing, revokeShare, type Share } from '../api/shares.ts';
import { listMyOrders, cancelOrder, type Order } from '../api/orders.ts';
import { currentUser } from '../state/session.ts';
import { t, getLang } from '../i18n/index.ts';
import { APIRequestError } from '../api/client.ts';

// Asiakaspalaute 1.6.2026: "Tallennetut tilat" -välilehti poistettu — sitä ei
// ollut referenssimockupissa (vertailutyokalu.html). Jäljelle jäävät jaot ja
// tilaukset. Backend-endpoint /smt/saved-estates jää paikalleen käyttämättömänä.
type Tab = 'incoming' | 'outgoing' | 'orders';

// Asiakaspalaute 3.6.2026: vain admin ja työntekijä (level 4) saavat jakaa
// tiloja, joten "Tekemäni jaot" (outgoing) -välilehti näytetään vain heille.
// Peruskäyttäjät näkevät vain saadut jaot ja omat tilaukset.
function canShareEstates(): boolean {
  const u = currentUser();
  return !!u && (u.role === 'admin' || u.level === 4);
}

function visibleTabs(): Tab[] {
  return canShareEstates() ? ['incoming', 'outgoing', 'orders'] : ['incoming', 'orders'];
}

let activeTab: Tab = 'incoming';

export async function renderMyEstates(root: HTMLElement): Promise<void> {
  const u = currentUser();
  if (!u) {
    root.innerHTML = `<div class="simple-page"><div class="placeholder-card">${escapeHtml(t('my_estates_login_required'))}</div></div>`;
    return;
  }

  const tabs = visibleTabs();
  // If the active tab is no longer visible (e.g. a basic user landed on
  // 'outgoing' via a stale state), fall back to the first visible tab.
  if (!tabs.includes(activeTab)) activeTab = tabs[0];

  root.innerHTML = `
    <div class="simple-page">
      <h1 class="simple-page-title">${escapeHtml(t('view_my_estates_title'))}</h1>
      <div class="simple-page-sub">${escapeHtml(t('view_my_estates_sub'))}</div>
      <div class="admin-tabs-bar" id="myTabs">
        ${tabs.map(
          (tab) => `<button class="admin-tab-btn ${tab === activeTab ? 'active' : ''}" data-tab="${tab}">
            ${escapeHtml(t(`my_estates_tab_${tab}`))}
          </button>`,
        ).join('')}
      </div>
      <div id="myTabBody">${escapeHtml(t('view_admin_loading'))}</div>
    </div>
  `;

  root.querySelectorAll<HTMLButtonElement>('#myTabs .admin-tab-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tab = btn.dataset.tab as Tab;
      if (!tab || tab === activeTab) return;
      activeTab = tab;
      void renderMyEstates(root);
    });
  });

  await refreshActiveTab(root);
}

async function refreshActiveTab(root: HTMLElement): Promise<void> {
  const body = root.querySelector<HTMLDivElement>('#myTabBody')!;
  body.textContent = t('view_admin_loading');
  try {
    switch (activeTab) {
      case 'incoming':
        renderIncoming(body, await listIncoming());
        break;
      case 'outgoing':
        renderOutgoing(body, root, await listOutgoing(false));
        break;
      case 'orders':
        renderOrders(body, root, await listMyOrders());
        break;
    }
  } catch (e) {
    body.innerHTML = `<div class="placeholder-card" style="color:rgb(140,55,55)">${escapeHtml(formatErr(e as Error))}</div>`;
  }
}

function renderIncoming(body: HTMLElement, rows: Share[]): void {
  if (!rows.length) {
    body.innerHTML = `<div class="placeholder-card">${escapeHtml(t('my_estates_incoming_empty'))}</div>`;
    return;
  }
  body.innerHTML = `
    <div class="listing-table-wrap"><table class="listing-table">
      <thead><tr>
        <th>${escapeHtml(t('view_admin_th_created'))}</th>
        <th>${escapeHtml(t('my_estates_th_estate'))}</th>
        <th>${escapeHtml(t('my_estates_th_municipality'))}</th>
        <th>${escapeHtml(t('my_estates_th_sharer'))}</th>
        <th>${escapeHtml(t('my_estates_th_message'))}</th>
        <th></th>
      </tr></thead>
      <tbody>
        ${rows.map((r) => `
          <tr>
            <td>${escapeHtml(formatDate(r.created_at))}</td>
            <td><a href="#/estates/${r.estate_id}">${escapeHtml(r.estate_name || `#${r.estate_id}`)}</a></td>
            <td>${escapeHtml(r.municipality || '—')}</td>
            <td>${escapeHtml(r.sharer_name || r.sharer_email || '—')}</td>
            <td>${escapeHtml(r.message || '')}</td>
            <td><a href="#/estates/${r.estate_id}">${escapeHtml(t('my_estates_open'))}</a></td>
          </tr>`).join('')}
      </tbody>
    </table></div>
  `;
}

function renderOutgoing(body: HTMLElement, root: HTMLElement, rows: Share[]): void {
  if (!rows.length) {
    body.innerHTML = `<div class="placeholder-card">${escapeHtml(t('my_estates_outgoing_empty'))}</div>`;
    return;
  }
  body.innerHTML = `
    <div class="listing-table-wrap"><table class="listing-table">
      <thead><tr>
        <th>${escapeHtml(t('view_admin_th_created'))}</th>
        <th>${escapeHtml(t('my_estates_th_estate'))}</th>
        <th>${escapeHtml(t('my_estates_th_recipient'))}</th>
        <th>${escapeHtml(t('my_estates_th_accepted'))}</th>
        <th></th>
      </tr></thead>
      <tbody>
        ${rows.map((r) => `
          <tr>
            <td>${escapeHtml(formatDate(r.created_at))}</td>
            <td>${escapeHtml(r.estate_name || `#${r.estate_id}`)}</td>
            <td>${escapeHtml(r.recipient_email)}</td>
            <td>${escapeHtml(r.accepted_at ? formatDate(r.accepted_at) : '—')}</td>
            <td><button class="btn-reject" data-share-id="${r.id}">${escapeHtml(t('my_estates_revoke'))}</button></td>
          </tr>`).join('')}
      </tbody>
    </table></div>
  `;

  body.querySelectorAll<HTMLButtonElement>('[data-share-id]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = Number(btn.dataset.shareId);
      if (!confirm(t('my_estates_revoke') + '?')) return;
      btn.disabled = true;
      try {
        await revokeShare(id);
        await refreshActiveTab(root);
      } catch (e) {
        alert(formatErr(e as Error));
        btn.disabled = false;
      }
    });
  });
}

function renderOrders(body: HTMLElement, root: HTMLElement, rows: Order[]): void {
  if (!rows.length) {
    body.innerHTML = `<div class="placeholder-card">${escapeHtml(t('my_estates_orders_empty'))}</div>`;
    return;
  }
  body.innerHTML = `
    <div class="listing-table-wrap"><table class="listing-table">
      <thead><tr>
        <th>${escapeHtml(t('view_admin_th_created'))}</th>
        <th>${escapeHtml(t('my_estates_th_estate'))}</th>
        <th>${escapeHtml(t('view_admin_th_status'))}</th>
        <th>${escapeHtml(t('my_estates_th_message'))}</th>
        <th>${escapeHtml(t('my_estates_th_decision_note'))}</th>
        <th></th>
      </tr></thead>
      <tbody>
        ${rows.map((r) => `
          <tr>
            <td>${escapeHtml(formatDate(r.created_at))}</td>
            <td><a href="#/estates/${r.estate_id}">${escapeHtml(r.estate_name || `#${r.estate_id}`)}</a></td>
            <td><span class="user-status-pill user-status-${escapeHtml(r.status)}">${escapeHtml(t('order_status_' + r.status))}</span></td>
            <td>${escapeHtml(r.message || '')}</td>
            <td>${escapeHtml(r.decision_note || '')}</td>
            <td>${
              r.status === 'pending'
                ? `<button class="btn-reject" data-order-id="${r.id}">${escapeHtml(t('my_estates_cancel_order'))}</button>`
                : ''
            }</td>
          </tr>`).join('')}
      </tbody>
    </table></div>
  `;

  body.querySelectorAll<HTMLButtonElement>('[data-order-id]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = Number(btn.dataset.orderId);
      if (!confirm(t('my_estates_cancel_order') + '?')) return;
      btn.disabled = true;
      try {
        await cancelOrder(id);
        await refreshActiveTab(root);
      } catch (e) {
        alert(formatErr(e as Error));
        btn.disabled = false;
      }
    });
  });
}

function formatDate(s: string | undefined | null): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString(getLang() === 'en' ? 'en-GB' : 'fi-FI');
  } catch {
    return s;
  }
}
function formatErr(e: Error): string {
  if (e instanceof APIRequestError) return e.body.error || e.message;
  return e.message;
}
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
